import { Star } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import type { ItineraryCompanion } from "@/lib/types";

export const ADDED_BY_NONE = "__none__";

interface ItineraryItemExtrasProps {
  companions: ItineraryCompanion[];
  addedBy: string;
  onAddedByChange: (value: string) => void;
  isAnchor: boolean;
  onAnchorToggle: () => void;
}

/**
 * The "added by" companion picker + anchor-event toggle for the item dialog.
 * Split out of ItineraryItemDialog to keep each file under the 300-line cap.
 */
export default function ItineraryItemExtras({
  companions,
  addedBy,
  onAddedByChange,
  isAnchor,
  onAnchorToggle,
}: ItineraryItemExtrasProps) {
  const resolved = companions.filter((c) => c.traveler_id && c.display_name);

  return (
    <>
      {resolved.length > 0 && (
        <div className="space-y-1.5">
          <Label htmlFor="it-by">Added by</Label>
          <Select value={addedBy} onValueChange={onAddedByChange}>
            <SelectTrigger id="it-by">
              <SelectValue placeholder="No one in particular" />
            </SelectTrigger>
            <SelectContent className="z-50">
              <SelectItem value={ADDED_BY_NONE}>No one in particular</SelectItem>
              {resolved.map((c) => (
                <SelectItem key={c.traveler_id!} value={c.traveler_id!}>
                  {c.display_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <button
        type="button"
        onClick={onAnchorToggle}
        className={
          "flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors " +
          (isAnchor
            ? "border-amber-300 bg-amber-50 text-amber-800"
            : "border-border bg-card text-muted-foreground hover:bg-muted")
        }
        aria-pressed={isAnchor}
      >
        <span
          className={
            "flex h-5 w-5 items-center justify-center rounded-full " +
            (isAnchor ? "bg-amber-100 text-amber-600" : "bg-muted")
          }
        >
          <Star
            className={"h-3 w-3" + (isAnchor ? " fill-amber-400" : "")}
            aria-hidden="true"
          />
        </span>
        <span className="font-medium">
          This is the anchor event
          <span className="ml-1 font-normal opacity-70">
            (the thing the trip is built around)
          </span>
        </span>
      </button>
    </>
  );
}
